'use client';

import { t } from 'i18next';
import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';

import { Box, Modal, Button, IconButton, Typography } from '@mui/material';

// import { googleAddressPasteSchema } from 'src/utils/validations';
import { checkIsCitySudair, filteredPredictionFromTypes } from 'src/utils/helper';

import { CloseIcon } from 'src/theme/core/components/mui-x-data-grid';
import {
  getLocationByCord,
  getLocationPrediction,
  useGetLongURLFromShort,
} from 'src/api/location/location';

import { Form, Field } from 'src/components/hook-form';
import { createSchemas } from 'src/utils/use-validations';

const styles = {
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', md: '50%' },
    // height: '90%',
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    p: 2,
  },
  keyValueContainer: { display: 'flex', gap: 2, width: '100%', justifyContent: 'space-between' },
  key: { color: 'text.secondary' },
  value: { typography: 'subtitle2', textAlign: 'right' },
};

export function GoogleAddressPasteModal({
  open,
  onClose,
  onPlaceSelect,
  inputType,
  setGoogleMapsLinkData,
  setIsAddressPasted,
}) {
  const [loading, setLoading] = useState(false);
  const defaultValues = {
    address: '',
  };

  const { googleAddressPasteSchema } = createSchemas();
  const methods = useForm({
    resolver: zodResolver(googleAddressPasteSchema),
    defaultValues,
    mode: 'all',
  });

  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { isSubmitting, isValid },
  } = methods;

  const { mutateAsync, isPending } = useGetLongURLFromShort(watch('address'));

  useEffect(() => {
    if (open) {
      reset();
      setIsAddressPasted(false);
    }
  }, [open, reset]);

  const onSubmit = handleSubmit(async (data) => {
    const extractedLongUrl = data?.address?.includes('maps.app.goo.gl')
      ? await mutateAsync()
      : data?.address;

    let latLng = extractLatLng(extractedLongUrl);

    if (!latLng) {
      const placeName = extractPlaceName(extractedLongUrl);
      const locationResponse = await getLocationPrediction(placeName);
      latLng = locationResponse?.predictions?.[0]?.geometry?.location;
    }

    setLoading(true); // start loading
    try {
      const location = await getLocationByCord(`${latLng?.lat},${latLng?.lng}`);

      if (Object.keys(location || {}).length > 0 && location?.predictions?.length > 0) {
        // check if the city is Sudair, if yes, the function will return the city as Sudair or return the original city
        const selectedPrediction =
          checkIsCitySudair(location) ||
          filteredPredictionFromTypes(location) ||
          location?.predictions?.[0];

        const updatedPrediction = {
          ...selectedPrediction,
          geometry: {
            ...selectedPrediction?.geometry,
            location: {
              lat: Number(latLng?.lat),
              lng: Number(latLng?.lng),
            },
          },
        };

        onPlaceSelect(inputType, updatedPrediction);
      }
    } catch (err) {
      console.error('Error fetching location:', err);
    } finally {
      setLoading(false); // stop loading
      reset();
      setGoogleMapsLinkData((prevState) => ({
        ...prevState,
        modal: false,
      }));
      setIsAddressPasted(false);
    }
  });

  const extractLatLng = (url) => {
    try {
      // Case 1: multiple !3dLAT!4dLNG (pick the last one)
      const latMatches = [...url.matchAll(/!3d(-?\d+(\.\d+)?)/g)];
      const lngMatches = [...url.matchAll(/!4d(-?\d+(\.\d+)?)/g)];

      if (latMatches.length && lngMatches.length) {
        const lat = latMatches[latMatches.length - 1][1];
        const lng = lngMatches[lngMatches.length - 1][1];
        return { lat, lng };
      }

      // Case 2: URL contains @lat,lng
      const atIndex = url.indexOf('/@');
      if (atIndex !== -1 && url) {
        const coordsPart = url.substring(atIndex + 2).split(',');
        return { lat: coordsPart[0], lng: coordsPart[1] };
      }

      // Case 3: fallback regex lat,lng
      const coordMatch = url.match(/(-?\d+\.\d+),\s*(-?\d+\.\d+)/);
      if (coordMatch && url) {
        return { lat: coordMatch[1], lng: coordMatch[2] };
      }

      // Case 4: /search/lat,lng
      const extractedUrl = url;

      const searchMatch = extractedUrl.match(
        /\/search\/\s*(-?\d+(?:\.\d+)?)\s*,\s*\+?\s*(-?\d+(?:\.\d+)?)/
      );

      if (searchMatch && extractedUrl) {
        const coords = {
          lat: parseFloat(searchMatch[1]),
          lng: parseFloat(searchMatch[2]),
        };
        return coords;
      } else {
        console.log('❌ No match');
      }

      return null;
    } catch {
      return null;
    }
  };

  const extractPlaceName = (url = '') => {
    if (!url) return null;

    try {
      // -----------------------------------------
      // 1️⃣ Case: /place/<name>/
      // -----------------------------------------
      const placeMatch = url?.match(/\/place\/([^/]+)/);
      if (placeMatch && placeMatch?.[1]) {
        return decodeURIComponent(placeMatch?.[1].replace(/\+/g, ' '));
      }

      // --------------------------------------------------
      // 2️⃣ Case: search URL -> /search/<name>
      // --------------------------------------------------
      const searchMatch = url?.match(/\/search\/([^/?]+)/);
      if (searchMatch && searchMatch?.[1]) {
        return decodeURIComponent(searchMatch?.[1].replace(/\+/g, ' '));
      }

      // --------------------------------------------------
      // 3️⃣ Case: ?query=<name>
      // --------------------------------------------------
      const queryParamMatch = url?.match(/[?&]query=([^&]+)/);
      if (queryParamMatch && queryParamMatch?.[1]) {
        return decodeURIComponent(queryParamMatch?.[1].replace(/\+/g, ' '));
      }

      // --------------------------------------------------
      // 4️⃣ Case: Text after maps.google.com/maps/<NAME>
      // --------------------------------------------------
      const mapsMatch = url?.match(/\/maps\/([^/]+)/);
      if (mapsMatch && mapsMatch?.[1] && !mapsMatch?.[1].startsWith('@')) {
        return decodeURIComponent(mapsMatch?.[1].replace(/\+/g, ' '));
      }

      // --------------------------------------------------
      // 5️⃣ Fallback: Try to find human-readable text before params
      // --------------------------------------------------
      const fallbackMatch = url?.match(/maps\.google\.com\/[^?]+\/([^?]+)/);
      if (fallbackMatch && fallbackMatch?.[1]) {
        return decodeURIComponent(fallbackMatch?.[1].replace(/\+/g, ' '));
      }

      return null;
    } catch {
      return null;
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={styles.modal}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">{t('Paste_URL')}</Typography>
          <IconButton onClick={onClose} aria-label="close button">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ width: '100%', height: '90%' }}>
          <Form methods={methods} onSubmit={onSubmit}>
            <Box display="flex" flexDirection="column">
              <Field.Text
                name="address"
                multiline
                sx={{ mx: 'auto', mb: 2 }}
                label={t('Paste_URL')}
                minRows={4}
              />
              <Button
                variant="contained"
                color="primary"
                sx={{ mx: 'auto', width: 250, height: 50 }}
                type="submit"
                loading={loading || isPending}
                disabled={loading || !watch('address') || !isValid || isPending || isSubmitting}
              >
                {t('Search')}
              </Button>
            </Box>
          </Form>
        </Box>
      </Box>
    </Modal>
  );
}
